import { memo, useEffect, useRef, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import { previewOf } from './pictureOps'
import { Window } from './brushStudio'
import { BRIA_CREDITS } from '../../shared/services/briaService'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The Eraser card (Huanrui, 2026-10-27)
 * -------------------------------------------------------------
 * Wire a picture in; it shows in the card's window, and whatever is painted
 * over -- as many places as wanted, in as many strokes -- is taken out by
 * Bria Eraser, which draws what would be behind it. All of it in one run.
 *
 * THE REST IS THE ORIGINAL. The board lays Bria's picture over the original
 * only where it was painted, with a soft edge a little past the paint; every
 * other pixel is the original's own, at its full size (ProjectCanvas,
 * runEraser; routes/bria.ts).
 *
 * Run puts a frame with the loader to the right, wired to the card, and folds
 * the card. The strokes are kept with the board, as shares of the picture,
 * for the picture they were painted on.
 */

/** One stroke: its radius as a share of the picture's width, and its points as shares, x then y. */
export interface EraseStroke {
  r: number
  pts: number[]
}

export interface EraserNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  strokes?: EraseStroke[]
  /** The picture the strokes were painted on (its key): another picture starts clean. */
  strokesFor?: string
  runs?: string[]
  error?: string
  credits?: number
  madeCount?: number

  /* handed in by the board, never stored */
  picture?: { url: string; turn: number; key: string } | null
  onEdit?: (patch: Partial<EraserNodeData>) => void
  onRun?: () => void
}

/** The strokes that belong to the picture wired in now. */
export function strokesOf(card: { strokes?: EraseStroke[]; strokesFor?: string }, key: string | undefined): EraseStroke[] {
  return key && card.strokesFor === key ? card.strokes ?? [] : []
}

/** Paint the strokes onto a context whose units are the picture's pixels (w x h). */
export function paintStrokes(ctx: CanvasRenderingContext2D, strokes: EraseStroke[], w: number, h: number, grow = 0): void {
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  for (const stroke of strokes) {
    const r = stroke.r * w + grow
    const pts = stroke.pts
    if (pts.length < 2) continue
    ctx.lineWidth = r * 2
    ctx.beginPath()
    ctx.moveTo(pts[0]! * w, pts[1]! * h)
    if (pts.length === 2) ctx.lineTo(pts[0]! * w + 0.01, pts[1]! * h)
    for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i]! * w, pts[i + 1]! * h)
    ctx.stroke()
  }
}

const round = (n: number) => Math.round(n * 10000) / 10000

export const EraserNode = memo(function EraserNode({ data, selected }: NodeProps) {
  const card = data as EraserNodeData
  const isOpen = card.open !== false
  const picture = card.picture ?? null
  const mine = (card.runs ?? []).length
  const edit = (patch: Partial<EraserNodeData>) => card.onEdit?.(patch)
  const strokes = strokesOf(card, picture?.key)

  // The picture in the window: the board's own copy, made smaller.
  const previewKey = picture ? `${picture.url}|${picture.turn}` : ''
  const [preview, setPreview] = useState<{ key: string; canvas: HTMLCanvasElement } | null>(null)
  useEffect(() => {
    if (!picture || !isOpen || preview?.key === previewKey) return
    let gone = false
    previewOf(picture.url, picture.turn)
      .then((canvas) => {
        if (!gone) setPreview({ key: previewKey, canvas })
      })
      .catch((error: unknown) => console.warn('[WonderWorks] Eraser: the picture could not be shown', error))
    return () => {
      gone = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, isOpen])
  const source = preview?.key === previewKey ? preview.canvas : null

  const [brush, setBrush] = useState(30)
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null)
  const [draft, setDraft] = useState<EraseStroke | null>(null)
  const scale = useRef(1)
  /** The paint, kept between redraws: one canvas the window's picture size. */
  const paint = useRef<HTMLCanvasElement | null>(null)

  const drawn = draft ? [...strokes, draft] : strokes
  const render = (ctx: CanvasRenderingContext2D, s: number) => {
    scale.current = s
    if (!source || drawn.length === 0) return
    const layer = paint.current ?? document.createElement('canvas')
    paint.current = layer
    if (layer.width !== source.width || layer.height !== source.height) {
      layer.width = source.width
      layer.height = source.height
    }
    const lctx = layer.getContext('2d')
    if (!lctx) return
    lctx.clearRect(0, 0, layer.width, layer.height)
    lctx.strokeStyle = '#ff3b30'
    paintStrokes(lctx, drawn, source.width, source.height)
    // One layer, so where strokes cross is no darker than anywhere else.
    ctx.save()
    ctx.globalAlpha = 0.5
    ctx.drawImage(layer, 0, 0)
    ctx.restore()
  }
  const overlay = (ctx: CanvasRenderingContext2D, s: number) => {
    scale.current = s
    if (!hover) return
    ctx.save()
    ctx.lineWidth = 1.5 / s
    ctx.strokeStyle = 'rgba(255,255,255,0.95)'
    ctx.beginPath()
    ctx.arc(hover.x, hover.y, brush / 2 / s, 0, Math.PI * 2)
    ctx.stroke()
    ctx.restore()
  }

  const startStroke = (at: { x: number; y: number }) => {
    if (!source || !picture || mine > 0) return null
    const w = source.width
    const h = source.height
    const point = (p: { x: number; y: number }) => [round(Math.min(1, Math.max(0, p.x / w))), round(Math.min(1, Math.max(0, p.y / h)))]
    let line: EraseStroke = { r: round(brush / 2 / scale.current / w), pts: point(at) }
    setDraft(line)
    return {
      move: (to: { x: number; y: number }) => {
        const [x, y] = point(to)
        const n = line.pts.length
        // A point only when the brush has moved a little: a stroke stays light to keep.
        if (Math.hypot((x! - line.pts[n - 2]!) * w, (y! - line.pts[n - 1]!) * h) < Math.max(1.5, (line.r * w) / 4)) return
        line = { ...line, pts: [...line.pts, x!, y!] }
        setDraft(line)
      },
      up: () => {
        setDraft(null)
        edit({ strokes: [...strokes, line], strokesFor: picture.key, error: '' })
      },
    }
  }

  const why = !picture
    ? 'Connect a picture first'
    : strokes.length === 0
      ? 'Paint over what to take out'
      : ''

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'enode',
        'ernode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        picture ? 'cnode--lit' : '',
        mine > 0 ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          <span className="pnode__label">Paint over what to take out · as many places as you like</span>
          {!picture ? (
            <div className="enode__empty-window">Connect a picture</div>
          ) : !source ? (
            <div className="enode__empty-window">Loading the picture…</div>
          ) : (
            <Window
              source={source}
              frame={null}
              render={render}
              stamp={`${drawn.length}|${draft ? draft.pts.length : 0}|${strokes.length}`}
              overlay={overlay}
              onLeft={startStroke}
              cursor="none"
              onHover={setHover}
              sizing={
                mine > 0
                  ? undefined
                  : { size: brush, setSize: setBrush, min: 4, max: 120, at: (at) => at && setHover(at) }
              }
            />
          )}

          <div className="enode__brush nodrag nopan">
            <label className="enode__row">
              <span>Size</span>
              <input
                className="nodrag"
                type="range"
                min={4}
                max={120}
                value={brush}
                onPointerDown={(event) => event.stopPropagation()}
                onChange={(event) => setBrush(Number(event.target.value))}
              />
              <em>{brush} px</em>
            </label>
            <p className="xnode__hint">Or hold Alt and drag with the right button: left and right for the size.</p>
            <div className="pnode__chips">
              <button
                type="button"
                className="pnode__chip"
                disabled={strokes.length === 0}
                onClick={() => edit({ strokes: strokes.slice(0, -1), strokesFor: picture?.key })}
                title="Take the last stroke back"
              >
                Undo stroke
              </button>
              <button
                type="button"
                className="pnode__chip"
                disabled={strokes.length === 0}
                onClick={() => edit({ strokes: [], strokesFor: picture?.key })}
                title="Take all the paint off"
              >
                Clear
              </button>
            </div>
          </div>
          <p className="xnode__hint">Only what is painted changes; the rest stays the original, at full size.</p>
          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || mine > 0 ? (
        <button
          type="button"
          className="cnode__run pnode__run nodrag"
          onClick={() => card.onRun?.()}
          disabled={Boolean(why) || !isOpen}
          title={why || 'Take out what is painted'}
        >
          {!isOpen ? (
            'Running'
          ) : (
            <>
              Run
              <span className="pnode__credits">about {BRIA_CREDITS.erase} credits</span>
            </>
          )}
          {mine > 0 ? <span className="pnode__credits">· {mine} going</span> : null}
        </button>
      ) : null}

      {mine === 0 && card.credits ? (
        <span className="pnode__spent">
          {card.madeCount ?? 0} made · {card.credits} credits
        </span>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
